import logger from '@ayocore/platform/common/helpers/logger';
import {
  activityMentionRegistry,
  type ActivityMentionThreadContext,
  type ActivityScopeMentionItem,
} from '@ayocore/activity/client/extensions';
import { useDebouncedSearchWithCache } from './useDebouncedSearchWithCache';

type UseActivityScopeMentionSearchParams = {
  enabled: boolean;
  query: string;
  threadEntityType?: string | null;
  threadEntityUuid?: string | null;
};

type UseActivityScopeMentionSearchResult = {
  scopeMentionItems: ActivityScopeMentionItem[];
  scopeMentionLoading: boolean;
};

/**
 * Поиск для `#`-меншна через реестр scope-провайдеров Activity.
 *
 * Активный провайдер — первый с `isAvailable(ctx)`; если провайдера нет,
 * поиск отключён (`#` в этом треде не работает — graceful fallback).
 */
export function useActivityScopeMentionSearch({
  enabled,
  query,
  threadEntityType,
  threadEntityUuid,
}: UseActivityScopeMentionSearchParams): UseActivityScopeMentionSearchResult {
  const ctx: ActivityMentionThreadContext = { threadEntityType, threadEntityUuid };
  const provider = activityMentionRegistry.resolveScopeProvider(ctx);
  const normalizedQuery = query.trim().toLowerCase();

  const { results, loading } = useDebouncedSearchWithCache<ActivityScopeMentionItem>({
    enabled: enabled && provider != null,
    cacheKey: `${provider?.id ?? 'none'}::${threadEntityType ?? ''}::${threadEntityUuid ?? ''}::${normalizedQuery}`,
    fetcher: async () => (provider ? provider.search(normalizedQuery, ctx) : []),
    onError: error => {
      logger.warn('[useActivityScopeMentionSearch] Scope mention search failed', {
        provider_id: provider?.id,
        query: normalizedQuery,
        threadEntityType,
        threadEntityUuid,
        error,
      });
    },
  });

  return {
    scopeMentionItems: results,
    scopeMentionLoading: loading,
  };
}
